import {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {Link, useHistory} from "react-router-dom";
import {css, Theme, useTheme} from "@emotion/react";
import {Button} from "@pg-design/button";
import {Col, Container, Row} from "@pg-design/grid";
import {calculateRemSize, flexDirection, mb, mt, onDesktop, ph, pt, w100} from "@pg-design/helpers-css";
import {getTextVariantStyleFor} from "@pg-design/text";
import {Text} from "@pg-design/text";

import {AccessStatus, useAccessStatus} from "../../../../../../utils/hooks/useAccessStatus";
import {chartsRoutes} from "../../../../common/app/routing/charts_routes";
import {breakpoints} from "../../../../common/app/styles/breakpoints";
import {withDateGuard} from "../../../../components/hoc/withDateGuard";
import {LandingMicroCopyList, microCopyListUnderCta} from "../landingMicroCopy";

import heroDesktop from "../../../../public/Property 1=desktop.png";
import heroMobile from "../../../../public/Property 1=mobile.png";

export const LANDING_BIG_DATA_CONTACT_SECTION_ID = "landing-big-data-contact";

const scrollToContactForm = () => {
    document.getElementById(LANDING_BIG_DATA_CONTACT_SECTION_ID)?.scrollIntoView({behavior: "smooth"});
};

export const Hero = withDateGuard(() => {
    const {
        t,
        i18n: {language}
    } = useTranslation();
    const theme = useTheme();
    const history = useHistory();
    const accessStatus = useAccessStatus();
    const routes = chartsRoutes[language] ?? chartsRoutes.pl;
    const pricingPath = routes.pricing;

    const stats = useMemo(
        () => [
            t("landing.analyticalPlatformHero.stat1"),
            t("landing.analyticalPlatformHero.stat2"),
            t("landing.analyticalPlatformHero.stat3"),
            t("landing.analyticalPlatformHero.stat4"),
            t("landing.analyticalPlatformHero.stat5")
        ],
        [t]
    );

    const handlePrimaryCtaClick = () => {
        if (accessStatus === AccessStatus.FULL) {
            history.push(routes.properties.root);
            return;
        }
        scrollToContactForm();
    };

    return (
        <Container css={wrapper} as="section">
            <Row>
                <Col sm={4} md={6} lg={8} css={heroTextCol}>
                    <div css={contentWrapper}>
                        <Text variant="headline_2" css={mb(2)}>
                            {t("landing.analyticalPlatformHero.title")}
                        </Text>
                        <Text variant="headline_5" css={mb(3)}>
                            {t("landing.analyticalPlatformHero.lead")}
                        </Text>
                        <div css={ctaBlock}>
                            <div css={ctaActions}>
                                <Button
                                    variant="filled_primary"
                                    size="big"
                                    css={ctaButton}
                                    onClick={handlePrimaryCtaClick}
                                >
                                    {t(
                                        accessStatus === AccessStatus.FULL
                                            ? "landing.analyticalPlatformHero.ctaPrimaryLoggedIn"
                                            : "landing.analyticalPlatformHero.ctaPrimary"
                                    )}
                                </Button>
                                <Link to={pricingPath} css={ctaLink}>
                                    <Button variant="outlined_secondary" size="big" css={ctaButton}>
                                        {t("landing.analyticalPlatformHero.ctaSecondary")}
                                    </Button>
                                </Link>
                            </div>
                            <LandingMicroCopyList items={stats} listCss={microCopyListUnderCta} />
                        </div>
                    </div>
                </Col>

                <Col sm={4} md={6} lg={8}>
                    <figure css={mockupFigure}>
                        <picture>
                            <source media={`(min-width: ${theme.breakpoints.md})`} srcSet={heroDesktop} />
                            <img css={mockupImg} src={heroMobile} alt={t("landing.analyticalPlatformHero.mockupAlt")} />
                        </picture>
                    </figure>
                </Col>
            </Row>
        </Container>
    );
});

/** Na mobile: kolumna flex + space-around, żeby blok tekstu + CTA rozłożyły się w pionie i rozciągały na szerokość. */
const heroTextCol = css`
    display: flex;
    ${flexDirection("column")};
    min-height: clamp(16rem, 42vh, 26rem);

    ${onDesktop(css`
        display: block;
        min-height: 0;
    `)}
`;

const contentWrapper = (theme: Theme) => css`
    ${w100};
    flex: 1;
    display: flex;
    ${flexDirection("column")};
    justify-content: space-around;
    align-items: stretch;

    ${onDesktop(css`
        flex: none;
        display: block;
    `)}

    h1 {
        ${mt(0)};
        ${mb(5)};
        ${getTextVariantStyleFor("headline_1")(theme)};
    }

    p {
        ${mb(6)};
        ${getTextVariantStyleFor("body_copy_0")(theme)};
    }
`;

const ctaBlock = css`
    display: flex;
    ${flexDirection("column")};
    align-items: center;
    ${w100};

    ${onDesktop(css`
        align-items: flex-start;
    `)}
`;

const ctaActions = css`
    display: flex;
    ${flexDirection("column")};
    gap: ${calculateRemSize(2)};
    ${w100};

    @media (min-width: ${breakpoints.sm}) {
        ${flexDirection("row")};
        flex-wrap: wrap;
        align-items: stretch;
    }
`;

const ctaLink = css`
    display: block;
    width: 100%;
    text-decoration: none;

    @media (min-width: ${breakpoints.xxs}) {
        display: inline-block;
        width: auto;
    }
`;

const ctaButton = css`
    ${ph(6)};
    text-transform: uppercase;
    font-weight: 700;
    border-radius: 9999px;
    width: 100%;

    @media (min-width: ${breakpoints.xxs}) {
        width: auto;
        min-width: 20.8rem;
    }
`;

const wrapper = (theme: Theme) => css`
    ${pt(3)};
    ${mb(8)};

    ${onDesktop(css`
        ${pt(6)};
        ${mb(9)};
    `)};

    @media (min-width: ${theme.breakpoints.lg}) {
        ${pt(14)}
    }
`;

const mockupFigure = (theme: Theme) => css`
    margin: 0;
    ${mt(6)};

    ${onDesktop(css`
        ${mt(0)};
        display: flex;
        justify-content: center;
        align-items: center;
    `)};

    @media (min-width: ${theme.breakpoints.lg}) {
        justify-content: flex-end;
    }
`;

const mockupImg = css`
    display: block;
    width: 100%;
    height: auto;
`;
